import { ArrowRight, Check } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SectionLabel } from '@/features/landing/components/SectionLabel'
import { FeeComparison } from '@/features/marketing/components/FeeComparison'
import { TierBadge } from '@/features/squadron/components/TierBadge'
import type { SubscriptionTier } from '@/lib/api/schemas'
import {
  TIER_ACCENT,
  TIER_ADMIN_CAP,
  TIER_ANNUAL_PRICE,
  TIER_MEMBER_CAP,
  TIER_MONTHLY_PRICE,
  annualSavingPercent,
} from '@/lib/squadron/tierMeta'
import { cn } from '@/lib/utils'

/**
 * Pricing — landing equivalent of the in-app `/app/squadron/tiers` page.
 *
 * Two design moves the previous version was missing:
 *
 *  1. **Use the actual tier brand names** (Bronze / Silver / Gold) rather
 *     than "Level 1 / 2 / 3". The in-app TierBadge already reads as
 *     BRONZE/SILVER/GOLD; the marketing page should do the same so a
 *     visitor learns one vocabulary, not two.
 *
 *  2. **Apply the premium gradient** from the strategy doc. The four
 *     cards used to look almost identical — flat surfaces with one
 *     accent on the "Most popular" tier. Now each tier visually escalates:
 *     FREE clean baseline → BRONZE warm metal → SILVER platinum +
 *     highlight strip → GOLD luminous with corner foil. Recruits glance
 *     and feel the ladder; owners see the path they're climbing.
 *
 * Every numeric value (prices, member caps, admin caps, annual saving)
 * is sourced from `tierMeta.ts` so this page can never drift out of sync
 * with what the app actually offers.
 */

interface TierCardData {
  tier: SubscriptionTier
  /** One-line headline below the brand name — leads with the USP that
   *  defines this tier, not a feature list. */
  pitch: string
  /** 3-4 marquee features. Kept short on purpose; the full feature
   *  matrix lives inside the app on /app/squadron/tiers. */
  features: string[]
  cta: { label: string; to: string }
  /** Recommended pick — gets the "Most popular" badge + lifted scale. */
  highlighted?: boolean
}

const TIERS: TierCardData[] = [
  {
    tier: 'FREE',
    pitch: 'Get your squad in and running. No card, no catch.',
    features: [
      'Verified squad badge',
      'Roster & role management',
      'Sessions, meetups, announcements',
      'Discord & Twitch linking',
    ],
    cta: { label: 'Get started', to: '/signup' },
  },
  {
    tier: 'LEVEL_1',
    pitch: 'Start charging dues and running real tournaments. Your squad starts looking the part.',
    features: [
      'Tournaments — SE / DE / Swiss / round-robin',
      'Member fees via Stripe Connect',
      'Squadron logo + growth analytics',
      'Monthly PDF reports',
    ],
    cta: { label: 'Start Bronze', to: '/signup?tier=level_1' },
  },
  {
    tier: 'LEVEL_2',
    pitch: 'Bigger roster, cross-squadron play, more room to run the show.',
    features: [
      'Cross-squadron tournaments (coming soon)',
      'Up to 4 public events / month',
      'Everything in Bronze',
    ],
    cta: { label: 'Start Silver', to: '/signup?tier=level_2' },
    highlighted: true,
  },
  {
    tier: 'LEVEL_3',
    pitch: 'For full organisations: paid pots, top billing, the works.',
    features: [
      'Paid tournament pots (entry + prizes)',
      'Up to 10 public events / month',
      'Everything in Silver',
      'Priority support',
    ],
    cta: { label: 'Start Gold', to: '/signup?tier=level_3' },
  },
]

export function Pricing() {
  return (
    <section
      id="pricing"
      className="relative border-b border-border/60 px-6 py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 flex flex-col items-start gap-6 md:mb-16">
          <SectionLabel index="06" label="Pricing" />
          <div className="flex w-full flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <h2 className="font-display max-w-2xl text-4xl leading-[1.05] font-semibold text-balance md:text-6xl">
              Four tiers. <span className="gradient-text">One ladder</span>.
            </h2>
            {/* Fee framing per MARKETING_COMPLIANCE.md — no unverifiable
                superlatives, competitors NAMED rather than generalised (a
                "5–10%" band would contradict Join It's 2–3% row in the
                FeeComparison table below), and the payout-side deductions
                disclosed, not waved off as "separate". Figures dated; the
                full dated table renders just below. */}
            <p className="max-w-md text-base text-fg-muted text-pretty">
              Flat per-squadron rate. No per-member meter. The free tier
              actually does things, and e404 takes 0.4% of member dues — a
              fraction of what platforms like Patreon (10%) or Ko-fi (5%)
              charge, as of July 2026. Card processing and VAT come out of
              the owner’s payout, and the app shows you that exact split
              before you set a fee.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {TIERS.map((data) => (
            <TierCard key={data.tier} data={data} />
          ))}
        </div>

        <div className="mx-auto mt-12 max-w-2xl">
          <FeeComparison />
        </div>

        <p className="mt-10 text-center font-display text-xs tracking-[0.2em] text-fg-subtle uppercase">
          All prices in GBP · cancel any time · Stripe-secured
        </p>
      </div>
    </section>
  )
}

function TierCard({ data }: { data: TierCardData }) {
  const { tier, pitch, features, cta, highlighted } = data
  const accent = TIER_ACCENT[tier]
  const isPaid = tier !== 'FREE'
  const monthly = TIER_MONTHLY_PRICE[tier]
  const annual = TIER_ANNUAL_PRICE[tier]
  const saving = annualSavingPercent(tier)
  const memberCap = TIER_MEMBER_CAP[tier]
  const adminCap = TIER_ADMIN_CAP[tier]

  // Visual escalation per tier — direct application of the premium
  // gradient strategy. Each step adds a layer of "earned" texture.
  const isSilver = tier === 'LEVEL_2'
  const isGold = tier === 'LEVEL_3'
  const borderAlpha = tier === 'FREE' ? '' : tier === 'LEVEL_1' ? '66' : '99'
  const cardShadow = isGold
    ? `0 0 40px -18px ${accent}99`
    : isSilver
      ? `0 0 28px -16px ${accent}80`
      : tier === 'LEVEL_1' && highlighted
        ? `0 0 32px -16px ${accent}66`
        : undefined

  return (
    <article
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-2xl border bg-surface p-6 transition-[transform,border-color] duration-200',
        highlighted ? 'lg:scale-[1.02]' : 'hover:-translate-y-0.5',
      )}
      style={{
        borderColor: isPaid ? `${accent}${borderAlpha}` : undefined,
        boxShadow: cardShadow,
      }}
    >
      {/* Top accent strip — escalates per tier. FREE has none; BRONZE a
          short fade; SILVER full-width; GOLD solid edge-to-edge. */}
      {isPaid && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
          style={{
            background:
              tier === 'LEVEL_1'
                ? `linear-gradient(90deg, ${accent}, ${accent}66 60%, transparent)`
                : tier === 'LEVEL_2'
                  ? `linear-gradient(90deg, ${accent}, ${accent}99 70%, ${accent}33)`
                  : `linear-gradient(90deg, ${accent}, ${accent}CC, ${accent})`,
          }}
        />
      )}

      {/* SILVER+ inset highlight — the "machined edge" of the medal. */}
      {(isSilver || isGold) && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-3 top-[2px] h-px"
          style={{
            backgroundImage:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
          }}
        />
      )}

      {/* GOLD corner foil — picks up the accent at the top-right so the
          card reads as a single luminous medallion rather than a tile. */}
      {isGold && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at top right, ${accent}1F, transparent 55%)`,
          }}
        />
      )}

      {/* Header — tier badge + "Most popular" if applicable */}
      <div className="relative mb-5 flex items-center justify-between">
        <TierBadge tier={tier} size="md" />
        {highlighted && <Badge variant="primary">Most popular</Badge>}
      </div>

      {/* Price block */}
      <div className="relative mb-2 flex items-baseline gap-1.5">
        <span className="font-display text-xs text-fg-subtle">£</span>
        <span
          className="font-display text-6xl leading-none font-bold tracking-tighter tabular-nums"
          style={isPaid ? { color: accent } : { color: 'var(--color-fg)' }}
        >
          {monthly}
        </span>
        <span className="font-display text-sm text-fg-muted">
          {tier === 'FREE' ? 'forever' : '/ month'}
        </span>
      </div>
      {isPaid && (
        <p className="relative mb-5 text-[11px] text-fg-subtle">
          or £{annual}/yr {saving > 0 && <>· save ~{saving}%</>}
        </p>
      )}
      {!isPaid && <span className="mb-5" aria-hidden />}

      {/* Pitch — leads with the USP. */}
      <p className="relative mb-6 text-sm text-fg-muted text-pretty">{pitch}</p>

      {/* Caps block — member + admin scaling, surfaced together so the
          ladder reads numerically as well as chromatically. */}
      <dl className="relative mb-6 grid grid-cols-2 gap-2 rounded-lg border border-border bg-bg/40 p-3">
        <div>
          <dt className="font-display text-[10px] tracking-[0.18em] text-fg-subtle uppercase">
            Members
          </dt>
          <dd className="font-display mt-0.5 text-xl font-bold tabular-nums text-fg">
            up to {memberCap}
          </dd>
        </div>
        <div>
          <dt className="font-display text-[10px] tracking-[0.18em] text-fg-subtle uppercase">
            Admin seats
          </dt>
          <dd
            className="font-display mt-0.5 text-xl font-bold tabular-nums"
            style={isPaid ? { color: accent } : { color: 'var(--color-fg)' }}
          >
            {adminCap}
          </dd>
        </div>
      </dl>

      {/* Features */}
      <ul className="relative mb-8 flex flex-1 flex-col gap-2.5 text-sm">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-fg-muted">
            <Check
              className="mt-0.5 size-4 shrink-0"
              style={isPaid ? { color: accent } : { color: 'var(--color-fg-subtle)' }}
              strokeWidth={2.5}
              aria-hidden
            />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        asChild
        variant={highlighted ? 'secondary' : 'outline'}
        size="md"
        className="relative w-full"
      >
        <Link to={cta.to}>
          {cta.label}
          <ArrowRight className="size-4" aria-hidden />
        </Link>
      </Button>
    </article>
  )
}
